// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Table, TableBody, ThemeProvider, Typography } from '@mui/material';
import { AddCircleOutlineOutlined as AddIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';

import { FHButton, FHFilterAndSort, FHWarning } from '../../base';
import TimesheetEditRow from './TimesheetEditRow';
import { timesheetActions, utilizationActions } from '../../../store';
import {
  filterTimesheetTherapy,
  getFilterConfig,
  getInitialTimesheetState,
  nonTherapyDataTransformer,
  THERAPY_HOURS_LABEL,
  NON_THERAPY_HOURS_LABEL,
  therapyDataTransformer,
} from './utils';
import { FORTA_BLUE } from '../../../utils/colors';
import { getUtcOffset } from '../../../utils/common';
import { timesheetTableTheme } from './timesheetTableTheme';
import { timesheetTableOptions } from './TableRenderOptions';
import { timesheetColumns, timesheetFilter } from './config';
import { areUserRolesIn, parseIntOrReturnString } from '../../../helpers';
import { Roles } from '../../../constants';
import { ProviderStatusEnum, TIMESHEET_OPTIONS } from '../../../pages/constants';

import type {
  IFilterAndSortItem,
  IFilterAndSortOption,
  IFilterOutput,
  ITimesheetTransformedData,
} from '../../../utils/types';
import type { RootState } from '../../../store';

interface ITimesheetTableProps {
  loadSessionsByDefault: boolean;
  selectedPayPeriod: Array<string>;
  isReadOnlyView: boolean;
  onSessionFilterChange: (clientFilter: IFilterAndSortOption | null) => void;
}

const TimesheetTable = ({
  loadSessionsByDefault,
  selectedPayPeriod,
  onSessionFilterChange,
  isReadOnlyView,
}: ITimesheetTableProps) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const {
    list: timesheetEntries,
    payPeriods,
    removeNewTimesheet,
    isAddingTimesheet,
    editingTimesheetId,
    isPayPeriodEditable,
    loading: timesheetsLoading,
  } = useSelector((state: RootState) => state.timesheets);
  const { list: clients = [] } = useSelector((state: RootState) => state.clients);
  const { list: serviceCodes = [] } = useSelector((state: RootState) => state.serviceCodes);
  const { list: sessions = [], loading: sessionsLoading } = useSelector(
    (state: RootState) => state.sessions,
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const { provider } = useSelector((state: RootState) => state.providers);

  const isProviderInTraining =
    provider?.status === ProviderStatusEnum.TRAINING &&
    TIMESHEET_OPTIONS.some(
      (option) =>
        option.label === ProviderStatusEnum.TRAINING &&
        areUserRolesIn(user?.roles, option.rolesWithAccess),
    );

  const [colDefs, setColDefs] = useState(timesheetColumns);
  const [columnOptions, setColumnOptions] = useState(() => timesheetTableOptions(isReadOnlyView));
  const [timesheetsNonTherapy, setTimesheetsNonTherapy] = useState<
    Array<ITimesheetTransformedData>
  >([]);
  const [timesheetsTherapy, setTimesheetsTherapy] = useState<Array<ITimesheetTransformedData>>([]);
  const [combinedTimesheets, setCombinedTimesheets] = useState<Array<ITimesheetTransformedData>>(
    [],
  );
  const [filteredTimesheets, setFilteredTimesheets] = useState<Array<ITimesheetTransformedData>>(
    [],
  );
  const [savedTimesheetLength, setSavedTimesheetLength] = useState<number>(0);
  const [newTimesheet, setNewTimesheet] = useState<ITimesheetTransformedData | null>(null);
  const [latestPastPayPeriod, setLatestPastPayPeriod] = useState<string[]>([]);
  const [currentPayPeriod, setCurrentPayPeriod] = useState<string[]>([]);
  const [filterOptions, setFilterOptions] = useState<{ [name: string]: IFilterAndSortItem }>({});
  const [filterOutput, setFilterOutput] = useState<IFilterOutput>({});
  const [defaultSelectedFilters, setDefaultSelectedFilters] = useState<IFilterOutput>({});
  const [sessionFilterUserSelection, setSessionFilterUserSelection] = useState(false);
  const [disableFilter, setDisableFilter] = useState<boolean>(false);

  const showTypeFilter = !areUserRolesIn(user?.roles, [Roles.Bcba]);

  const roleNeedsClient = useMemo(() => {
    return !TIMESHEET_OPTIONS.some(
      (option) => areUserRolesIn(user?.roles, option.rolesWithAccess) && option.isClientOptional,
    );
  }, [user?.roles]);

  const canAccessNonTherapyHours = useMemo(() => {
    return !!TIMESHEET_OPTIONS.some(
      (option) =>
        areUserRolesIn(user?.roles, option.rolesWithAccess) && option.label === 'Non-Therapy Hours',
    );
  }, [user?.roles]);

  const payPeriodText = useMemo(
    () =>
      `Pay period ${moment(selectedPayPeriod[0]).format('MMM DD')} - ${moment(
        selectedPayPeriod[1],
      ).format('MMM DD')}`,
    [selectedPayPeriod],
  );

  useEffect(() => {
    if (!isReadOnlyView) return;
    const newColDef = colDefs.map((col) => {
      if (col.name === 'provider_name') {
        return {
          name: 'provider_name',
          label: 'Provider',
          options: {
            filter: true,
            sort: true,
            display: true,
            filterOptions: {
              fullWidth: true,
            },
          },
        };
      }
      return col;
    });

    setColDefs(newColDef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnlyView]);

  useEffect(() => {
    if (editingTimesheetId) {
      setColumnOptions({ ...columnOptions, sort: false });
      setDisableFilter(true);
    } else {
      setColumnOptions({ ...columnOptions, sort: true });
      setDisableFilter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTimesheetId]);

  useEffect(() => {
    const sessionFilter = filterOutput[timesheetFilter.Type]?.find(
      (filter) => filter.id === THERAPY_HOURS_LABEL,
    );
    if (!sessionFilter) {
      onSessionFilterChange(null);
      setSessionFilterUserSelection(false);
      return;
    }
    setSessionFilterUserSelection(true);
    onSessionFilterChange(sessionFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOutput[timesheetFilter.Type]]);

  useEffect(() => {
    if (!(payPeriods?.past?.length > 0) || !serviceCodes?.length) return;
    const latest = payPeriods.past[payPeriods.past.length - 1];
    setLatestPastPayPeriod(latest);
    setCurrentPayPeriod(payPeriods.present);
    dispatch(timesheetActions.updateState({ key: 'isPayPeriodEditable', value: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriods, serviceCodes]);

  useEffect(() => {
    if (currentPayPeriod.length === 0 || selectedPayPeriod.length === 0) return;

    if (
      currentPayPeriod[0] === selectedPayPeriod[0] &&
      currentPayPeriod[1] === selectedPayPeriod[1]
    ) {
      dispatch(timesheetActions.updateState({ key: 'isPayPeriodEditable', value: true }));
    } else {
      const today = new Date();
      if (
        latestPastPayPeriod.length > 0 &&
        selectedPayPeriod[0] === latestPastPayPeriod[0] &&
        selectedPayPeriod[1] === latestPastPayPeriod[1] &&
        moment(today).isSameOrBefore(
          moment(payPeriods.past_payperiod_data_input_end_date).endOf('day'),
        )
      ) {
        dispatch(timesheetActions.updateState({ key: 'isPayPeriodEditable', value: true }));
      } else {
        dispatch(timesheetActions.updateState({ key: 'isPayPeriodEditable', value: false }));
      }
    }
    setDefaultSelectedFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayPeriod, currentPayPeriod, latestPastPayPeriod]);

  useEffect(() => {
    if (!timesheetEntries) return;
    const transformedData = nonTherapyDataTransformer(timesheetEntries, selectedPayPeriod);
    setTimesheetsNonTherapy(transformedData);
    if (timesheetEntries.length > savedTimesheetLength) {
      handleRemoveNewTimesheet();
    }
    setSavedTimesheetLength(timesheetEntries.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetEntries, selectedPayPeriod]);

  useEffect(() => {
    if (!sessions) return;
    const filteredSessions = filterTimesheetTherapy(sessions, isReadOnlyView, user?.email);
    const transformedData = therapyDataTransformer(filteredSessions, selectedPayPeriod);
    setTimesheetsTherapy(transformedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, selectedPayPeriod, isReadOnlyView]);

  useEffect(() => {
    const sortedData = [...timesheetsNonTherapy, ...timesheetsTherapy].sort(
      (a: ITimesheetTransformedData, b: ITimesheetTransformedData) => {
        return moment(b.date_start).isBefore(a.date_start) ? 1 : -1;
      },
    );
    setCombinedTimesheets(sortedData);
    setFilteredTimesheets(sortedData);
    const filterConfig = getFilterConfig(sortedData, isReadOnlyView, showTypeFilter);
    setFilterOptions(filterConfig);
    const defaultFilters: IFilterOutput = {};
    if (sessionFilterUserSelection) {
      defaultFilters[timesheetFilter.Type] = [
        {
          id: THERAPY_HOURS_LABEL,
          label: THERAPY_HOURS_LABEL,
          selected: true,
        },
      ];
    }
    if (!loadSessionsByDefault && timesheetsNonTherapy.length && showTypeFilter) {
      defaultFilters[timesheetFilter.Type] = defaultFilters[timesheetFilter.Type]
        ? [
            ...defaultFilters[timesheetFilter.Type],
            {
              id: NON_THERAPY_HOURS_LABEL,
              label: NON_THERAPY_HOURS_LABEL,
              selected: true,
            },
          ]
        : [
            {
              id: NON_THERAPY_HOURS_LABEL,
              label: NON_THERAPY_HOURS_LABEL,
              selected: true,
            },
          ];
    }
    setDefaultSelectedFilters(defaultFilters);
    setSessionFilterUserSelection(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetsNonTherapy, timesheetsTherapy, isReadOnlyView]);

  useEffect(() => {
    let filteredTimesheetData = [...combinedTimesheets];
    for (const filterName in filterOutput) {
      filteredTimesheetData = filteredTimesheetData.filter((timesheet) => {
        if (filterName === timesheetFilter.Bt) {
          return (
            filterOutput[filterName].some(
              (item) =>
                parseIntOrReturnString(item?.id) ===
                parseIntOrReturnString(timesheet.provider_id ?? ''),
            ) || filterOutput[filterName].length === 0
          );
        }
        if (filterName === timesheetFilter.Client) {
          return (
            filterOutput[filterName].some(
              (item) =>
                parseIntOrReturnString(item?.id) ===
                parseIntOrReturnString(timesheet.client_id ?? ''),
            ) || filterOutput[filterName].length === 0
          );
        }
        if (filterName === timesheetFilter.Type) {
          return (
            filterOutput[filterName].some((item) => {
              if (item?.id === NON_THERAPY_HOURS_LABEL) {
                return timesheet.service_code !== THERAPY_HOURS_LABEL;
              } else {
                return (
                  parseIntOrReturnString(item?.id) ===
                  parseIntOrReturnString(timesheet.service_code ?? '')
                );
              }
            }) || filterOutput[filterName].length === 0
          );
        }
        return true;
      });
    }
    setFilteredTimesheets(filteredTimesheetData);
    setColumnOptions({
      ...columnOptions,
      textLabels: {
        body: {
          noMatch: 'No matching records found for selected filter(s)',
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOutput]);

  useEffect(() => {
    if (
      !(timesheetFilter.Client in filterOutput && filterOutput[timesheetFilter.Client].length) ||
      !selectedPayPeriod?.length
    ) {
      dispatch(utilizationActions.clearState());
      return;
    }
    const startDate = moment(selectedPayPeriod[0]).format('YYYY-MM-DD');
    const endDate = moment(selectedPayPeriod[1]).format('YYYY-MM-DD');
    dispatch(
      utilizationActions.getUtilization({
        getAccessTokenSilently,
        clientIds: [filterOutput.client[0].id],
        startDate,
        endDate,
        utcOffset: getUtcOffset(moment(new Date())),
      }),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOutput[timesheetFilter.Client]]);

  useEffect(() => {
    if (!removeNewTimesheet) return;
    handleRemoveNewTimesheet();
    dispatch(timesheetActions.updateState({ key: 'removeNewTimesheet', value: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeNewTimesheet, selectedPayPeriod]);

  const handleRemoveNewTimesheet = () => {
    dispatch(timesheetActions.updateState({ key: 'isAddingTimesheet', value: false }));
    setColumnOptions({ ...columnOptions, sort: true });
    if (timesheetsNonTherapy?.length > 0 || timesheetsTherapy?.length > 0) {
      setDisableFilter(false);
    }
    setNewTimesheet(null);
  };

  const handleAddTimesheet = () => {
    if (
      (clients.length < 1 && roleNeedsClient && !isProviderInTraining) ||
      serviceCodes.length < 1 ||
      !payPeriods
    )
      return;
    const currentPayPeriodStartDate = new Date();
    const latestPastPayPeriod = payPeriods.past[payPeriods.past.length - 1];
    let newTimesheetData;
    if (
      latestPastPayPeriod?.length > 0 &&
      latestPastPayPeriod[0] === selectedPayPeriod[0] &&
      latestPastPayPeriod[1] === selectedPayPeriod[1] &&
      moment(currentPayPeriodStartDate).isSameOrBefore(
        moment(payPeriods.past_payperiod_data_input_end_date).endOf('day'),
      )
    ) {
      newTimesheetData = getInitialTimesheetState(serviceCodes, latestPastPayPeriod);
    } else {
      newTimesheetData = getInitialTimesheetState(serviceCodes);
    }
    setNewTimesheet(newTimesheetData);
    dispatch(timesheetActions.updateState({ key: 'isAddingTimesheet', value: true }));
    setColumnOptions({ ...columnOptions, sort: false });
    setDisableFilter(true);
  };

  return (
    <ThemeProvider theme={timesheetTableTheme}>
      <Box mb={-6}>
        <Typography fontSize={21} my={2} fontWeight={700} textAlign={'left'}>
          {payPeriodText}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 1 }}>
        <FHFilterAndSort
          filterSortOptions={filterOptions}
          disabled={disableFilter}
          selectedFilter={defaultSelectedFilters}
          chipWrapperId="timesheet-filter-chips"
          showResetButton
          onFilterChange={(data) => {
            setFilterOutput(data);
          }}
          buttonStyles={
            filterOutput &&
            Object.keys(filterOutput).length > 0 &&
            Object.keys(filterOutput).some((key) => filterOutput[key].length > 0)
              ? {}
              : {
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                }
          }
        />
      </Box>
      <Box id="timesheet-filter-chips" data-testid="filteredChip" sx={{ marginTop: '2px' }}></Box>
      {combinedTimesheets.length > 0 || newTimesheet ? (
        <>
          {combinedTimesheets.length > 0 && (
            <MUIDataTable
              title={''}
              data={filteredTimesheets}
              columns={colDefs}
              options={columnOptions}
            />
          )}
          {newTimesheet && (
            <Table sx={{ mt: -2 }}>
              <TableBody>
                <TimesheetEditRow timesheet={newTimesheet} onClose={handleRemoveNewTimesheet} />
              </TableBody>
            </Table>
          )}
        </>
      ) : (
        <>
          {combinedTimesheets.length === 0 && !(sessionsLoading || timesheetsLoading) && (
            <Typography
              fontSize={21}
              mb={3}
              textAlign={'left'}
              fontWeight={500}
              color={FORTA_BLUE.light1}
            >
              Attention: No entries found for the selected filter(s) for this time period
            </Typography>
          )}
        </>
      )}
      {clients.length < 1 && !isProviderInTraining && canAccessNonTherapyHours && (
        <FHWarning
          sxContainer={{ backgroundColor: 'transparent', ml: -1.5 }}
          content="Assign a clinical client to enable non-therapy hours."
        />
      )}
      <FHButton
        data-testid="addNontherapyHours"
        sx={{ height: 40, width: 'fit-content' }}
        startIcon={<AddIcon />}
        disabled={Boolean(
          isAddingTimesheet ||
          editingTimesheetId ||
          (clients.length < 1 && roleNeedsClient && !isProviderInTraining),
        )}
        hidden={!isPayPeriodEditable || isReadOnlyView}
        onClick={handleAddTimesheet}
      >
        Add
      </FHButton>
    </ThemeProvider>
  );
};

export default TimesheetTable;
